import { Bell, BookOpen, LogOut, ChartGantt, ChartLine, ChevronsLeft, CircleCheck, CircleQuestionMark, FolderKanban, Hourglass, Kanban, LayoutDashboard, Plus, Presentation, Settings } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { client, useClient } from "../lib/client.ts";
import { Avatar } from "./Badges.tsx";
import { NotificationsPanel } from "./NotificationsPanel.tsx";
import { Button } from "../ui/ring.ts";

interface NavItem {
  label: string;
  icon: ReactNode;
  to?: string;
  match?: RegExp;
}

// Same order as YouTrack's sidebar. Items without a route are shown greyed so the layout matches; they say so when clicked.
const MAIN: NavItem[] = [
  { label: "Issues", icon: <CircleCheck size={20} />, to: "/issues", match: /^\/(issues|issue\/)/ },
  { label: "Dashboards", icon: <LayoutDashboard size={20} />, to: "/dashboards", match: /^\/dashboards?(\/|$)/ },
  { label: "Agile Boards", icon: <Kanban size={20} />, to: "/agiles", match: /^\/(agiles|boards?)(\/|$)/ },
  { label: "Reports", icon: <ChartLine size={20} /> },
  { label: "Projects", icon: <FolderKanban size={20} />, to: "/admin/projects", match: /^\/admin\/projects/ },
  { label: "Knowledge Base", icon: <BookOpen size={20} />, to: "/articles", match: /^\/(articles|article\/)/ },
  { label: "Timesheets", icon: <Hourglass size={20} /> },
  { label: "Gantt Charts", icon: <ChartGantt size={20} /> },
  { label: "Whiteboards", icon: <Presentation size={20} /> },
];

/** Left navigation + content. Collapsible like YouTrack's. */
export function Shell({ children, onCreate, onLoggedOut }: { children: ReactNode; onCreate: () => void; onLoggedOut: () => void }) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("gjw.nav") === "collapsed";
    } catch {
      return false;
    }
  });
  const toggle = (): void => {
    setCollapsed((c) => {
      try {
        localStorage.setItem("gjw.nav", c ? "open" : "collapsed");
      } catch {
        /* ignore */
      }
      return !c;
    });
  };
  const me = useClient((s) => s.me);
  const schema = useClient((s) => s.schema);
  const location = useLocation();
  const [notice, setNotice] = useState<string | null>(null);
  const [bell, setBell] = useState(false);
  const unread = useClient((s) => s.notifications.unread);
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    void client.loadNotifications();
    fetch("/api/version")
      .then((r) => (r.ok ? (r.json() as Promise<{ version: string }>) : null))
      .then((v) => v && setVersion(v.version))
      .catch(() => {});
  }, []);
  const logout = async (): Promise<void> => {
    await client.http.logout().catch(() => {});
    client.stop();
    onLoggedOut();
  };

  const item = (n: NavItem, extra = ""): ReactNode => {
    const active = n.match ? n.match.test(location.pathname) : false;
    const cls = `nav-item ${active ? "active" : ""} ${n.to ? "" : "disabled"} ${extra}`;
    const body = (
      <>
        <span className="shrink-0">{n.icon}</span>
        {!collapsed && <span>{n.label}</span>}
      </>
    );
    if (n.to) return <Link key={n.label} to={n.to} className={cls} title={collapsed ? n.label : undefined}>{body}</Link>;
    return (
      <button key={n.label} className={cls} title={collapsed ? n.label : undefined} onClick={() => setNotice(`${n.label} is not available yet.`)}>
        {body}
      </button>
    );
  };

  return (
    <div className="flex h-full">
      <nav className={`flex shrink-0 flex-col border-r border-line bg-nav p-2 ${collapsed ? "w-14" : "w-60"}`} aria-label="Main">
        <Link to="/issues" className="mb-3 flex items-center gap-2 px-2 py-2">
          <span className="flex h-8 w-8 items-center justify-center rounded bg-accent text-sm font-bold text-accent-ink">GJ</span>
          {!collapsed && <span className="text-lg font-semibold tracking-tight">GJWTrack</span>}
        </Link>
        {!collapsed && version && (
          <Link to="/releases" className={`-mt-3 mb-2 ml-12 self-start text-xs text-muted hover:text-ink ${location.pathname === "/releases" ? "text-ink" : ""}`} title="Release notes">
            v{version}
          </Link>
        )}
        <div className="flex flex-col gap-0.5">{MAIN.map((n) => item(n))}</div>
        <div className="flex-1" />
        <div className="flex flex-col gap-0.5">
          <button className="nav-item bg-surface" onClick={onCreate} title="Create issue">
            <Plus size={20} />
            {!collapsed && <span>Create</span>}
          </button>
          {item({ label: "Administration", icon: <Settings size={20} />, to: "/admin/projects", match: /^\/admin/ })}
          {item({ label: "Help", icon: <CircleQuestionMark size={20} /> })}
          <NotificationsPanel
            onToggle={setBell}
            anchor={
              <button className={`nav-item w-full ${bell ? "active" : ""}`} title="Notifications">
                <span className="relative shrink-0">
                  <Bell size={20} />
                  {unread > 0 && <span className="absolute -top-1.5 -right-2 rounded-full bg-danger px-1 text-[10px] leading-4 font-semibold text-white">{unread > 99 ? "99+" : unread}</span>}
                </span>
                {!collapsed && <span>Notifications</span>}
                {!collapsed && unread > 0 && <span className="ml-auto text-xs text-muted tabular-nums">{unread}</span>}
              </button>
            }
          />
          {me && (
            <div className="flex items-center">
              <Link to="/account" className={`nav-item min-w-0 flex-1 ${location.pathname === "/account" ? "active" : ""}`} title="Account">
                <Avatar schema={schema} userId={me.id} size={22} />
                {!collapsed && <span className="truncate">{me.name}</span>}
              </Link>
              {!collapsed && (
                <Button icon={LogOut} className="shrink-0" onClick={() => void logout()} title="Log out" />
              )}
            </div>
          )}
          <button className="nav-item text-muted" onClick={toggle} title={collapsed ? "Expand" : "Collapse"}>
            <ChevronsLeft size={20} className={collapsed ? "rotate-180" : ""} />
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </nav>
      <div className="flex min-w-0 flex-1 flex-col">{children}</div>
      {notice && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 rounded border border-line bg-surface px-3 py-2 shadow" role="status" onAnimationEnd={() => setNotice(null)}>
          {notice}
          <button className="ml-3 text-muted hover:text-ink" onClick={() => setNotice(null)}>×</button>
        </div>
      )}
    </div>
  );
}
